import type { AxiosRequestConfig, AxiosResponse } from "axios";
import axios from "axios";

import EnvConfig from "../../config/env";

import httpMethods from "./httpMethods";

// Extend Axios config to allow opting out of global side-effects
declare module "axios" {
  interface AxiosRequestConfig {
    skipAuthRedirect?: boolean;
    // Suppress the paywall:open event for background probes that silently fail
    // when a feature isn't available (e.g. site-sync status on free plan).
    skipPaywallEvent?: boolean;
  }
}

// Module-level active project ID — set by useAuth on login/preference load,
// read by query hooks to guard requests that require a project context.
let _activeProjectId: string | null = null;

export function setActiveProjectId(id: string | number | null | undefined): void {
  _activeProjectId = id != null ? String(id) : null;
}

export function getActiveProjectId(): string | null {
  return _activeProjectId;
}

// ─── Guide Me bypass ──────────────────────────────────────────────────────────
// While a Guide Me tour is active (or its preview-session switch is in
// flight), "[Guide Me]"-prefixed rows must be VISIBLE in the product's normal
// lists — the canonical Sample Project smoke-test cycle, and anything a user
// creates during a teach-by-creating tour step. The backend filters those rows
// out of every list/aggregate query UNLESS the request carries the
// `x-guide-me-bypass: 1` header. GuideTourContext holds this flag on for the
// whole duration of a tour via setGuideMeBypass(true/false).
let _guideMeBypass = false;

function guideMeBypassActive(): boolean {
  return _guideMeBypass;
}

export function setGuideMeBypass(on: boolean): void {
  _guideMeBypass = on;
}

// Default request timeout (ms). Prevents requests from hanging indefinitely
// when the backend or network stalls — surfaces as an ECONNABORTED error that
// callers can show/retry instead of leaving spinners and disabled buttons stuck.
const REQUEST_TIMEOUT_MS = 30_000;

// Create Axios instance
const apiClient = axios.create({
  baseURL: `${EnvConfig.serverURL}${EnvConfig.basePath}`.replace(/\/+$/, ""),
  withCredentials: true, // Ensures cookies like X-AUTH are sent
  timeout: REQUEST_TIMEOUT_MS,
});

// Request Interceptor
// Auth travels via the HttpOnly X-AUTH cookie (sent automatically because
// withCredentials is true). The token is intentionally NOT readable from JS, so
// there is no Authorization header to attach here for user sessions.
apiClient.interceptors.request.use(
  (config) => {
    if (guideMeBypassActive()) {
      config.headers.set("x-guide-me-bypass", "1");
    }
    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Guards against multiple simultaneous 401s triggering duplicate redirects
let isRedirectingToLogin = false;

function isUserNotFoundError(error: any): boolean {
  const payload = error?.response?.data ?? {};
  const message = String(payload.message ?? payload.error ?? payload?.detail ?? "");
  return /user\s*not\s*found/i.test(message) || /authenticated user not found/i.test(message);
}

// Response Interceptor
apiClient.interceptors.response.use(
  (response) => {
    // A successful response means the session is alive again — release the
    // redirect latch so a future genuine 401 can redirect once more.
    isRedirectingToLogin = false;
    // The X-AUTH session cookie is managed server-side as HttpOnly; the browser
    // stores/sends it automatically. We must not read or re-write it from JS
    // (a JS-readable copy would be XSS-exfiltratable).
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const skipRedirect = error.config?.skipAuthRedirect;
    // A deleted/suspended user (is_Deleted=1) gets a 401 with this code from the
    // auth middleware. Such a session must ALWAYS be bounced to login — even on
    // /onboarding paths — otherwise a user deleted while holding a live session
    // gets their 401 swallowed and is left stranded on the onboarding "Create
    // Organization" page instead of seeing the access-revoked message (#2084).
    const isSuspended = error.response?.data?.error === "account_suspended";
    const isMissingUser = isUserNotFoundError(error);

    // A missing user record is a broken session, not a normal auth expiry.
    // Force a clean login redirect only for that case so normal 401 expiry flows
    // (and onboarding/session UI) keep their existing behavior.
    if (
      status === 401 &&
      isMissingUser &&
      !skipRedirect &&
      !isRedirectingToLogin &&
      !window.location.pathname.startsWith("/login")
    ) {
      isRedirectingToLogin = true;
      window.location.replace("/login");
      setTimeout(() => {
        isRedirectingToLogin = false;
      }, 5_000);
      return Promise.reject(error);
    }

    // Redirect to login with returnUrl on session expiry (401), unless:
    //  - the request explicitly opted out (login, session-check, logout calls)
    //  - we're already on the login page
    //  - a redirect is already in progress
    //  - we're on an /onboarding path (except when the account was suspended)
    if (
      status === 401 &&
      !skipRedirect &&
      !isRedirectingToLogin &&
      !window.location.pathname.startsWith("/login") &&
      (!window.location.pathname.startsWith("/onboarding") || isSuspended)
    ) {
      isRedirectingToLogin = true;
      window.dispatchEvent(new CustomEvent("session-expired"));
      // Release the latch after a short window so the burst of concurrent 401s
      // is de-duped into a single redirect, while a later session expiry can
      // still trigger a redirect (previously this latch was never reset, so
      // only the very first 401 of the app's lifetime ever redirected).
      setTimeout(() => {
        isRedirectingToLogin = false;
      }, 5_000);
    }

    // 402 Payment Required — surface a paywall modal via a global DOM event.
    // The PaywallModal listens for `paywall:open` at the app root.
    // Skip for background probes that are expected to 402 silently (e.g. status checks).
    if (status === 402 && !error.config?.skipPaywallEvent) {
      const payload = error.response?.data;
      window.dispatchEvent(
        new CustomEvent("paywall:open", { detail: payload })
      );
    }

    // Build a concise, greppable label so a hanging/slow endpoint is
    // identifiable (the bare AxiosError doesn't show which call timed out).
    const method = (error.config?.method || "GET").toUpperCase();
    const reqUrl = error.config?.url || "(unknown url)";
    const isTimeout = error.code === "ECONNABORTED" || /timeout/i.test(error.message || "");

    if (isTimeout) {
      // A timeout against a reachable backend usually means the request is
      // queued behind other in-flight calls (browser caps ~6 connections per
      // host) or the endpoint is genuinely slow. Log one quiet line, not the
      // full error object, so a flood stays readable.
      console.warn(`[HTTP timeout ${REQUEST_TIMEOUT_MS}ms] ${method} ${reqUrl}`);
    } else {
      console.error(`Response interceptor error (${status ?? "no status"}): ${method} ${reqUrl}`, error);
    }
    return Promise.reject(error);
  }
);

// Main HTTP Client
const httpClient = async <T = any>(
  method: string,
  url: string,
  data?: any,
  options: AxiosRequestConfig = {}
): Promise<{ data: T; status: number; statusText: string }> => {
  const base = `${EnvConfig.serverURL}${EnvConfig.basePath}`.replace(/\/+$/, "");
  const fullUrl = url.startsWith("http") ? url : `${base}/${url.replace(/^\/+/, "")}`;

  const isGet = method === httpMethods.GET;
  const isMultipart = method === httpMethods.MULTIPART;
  const isMultipartPut = method === httpMethods.MULTIPART_PUT;

  const config: AxiosRequestConfig = {
    ...options,
    url: fullUrl,
    method: isMultipart ? "post" : isMultipartPut ? "put" : method.toLowerCase(),
    // File uploads can legitimately take longer than the default 30s — disable
    // the timeout so a large video/log upload over a slow connection doesn't
    // fail with ECONNABORTED before the server finishes receiving the file.
    ...(isMultipart || isMultipartPut ? { timeout: 0 } : {}),
    headers: {
      ...(!isGet && !isMultipart && !isMultipartPut ? { "Content-Type": "application/json" } : {}),
      ...options.headers,
    },
    ...(isGet ? { params: data } : { data }),
  };

  try {
    const response: AxiosResponse<T> = await apiClient.request(config);
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
    };
  } catch (error: any) {
    if (error.response?.status === 415) {
      console.error("Unsupported Media Type (415)", {
        headers: config.headers,
        data,
      });
    }
    // Other errors (incl. timeouts) are already logged once by the response
    // interceptor with method + URL — don't double-log the raw AxiosError here.
    throw error;
  }
};

export default httpClient;